import { useCallback, useEffect, useState } from 'react';
import { ZodIssueCode, z } from 'zod';
import {
	SfFigure2AndChildHoldinghandsIcon,
	SfFigureAndChildHoldinghandsIcon,
	SfFigureStandIcon,
	SfFigureWalkIcon,
} from '@ninja/icons/web';
import {
	EmptyValidationMessage,
	Path,
	RadioCardOption,
	ReactForm,
	addSeconds,
	getNow,
	isBefore,
	useReactForm,
} from '@ninja/utilities';
import { getIsoDateTime, getTzTodayDate, parseIsoDateTime } from '@ninja/utilities/dist/DateUtils';
import {
	CreateJourneyV2Error,
	GlobalAPIError,
	eidAndPersonalInfoCheck,
	eidAndPersonalInfoCheckError,
	postCreateJourneyV2,
	postUpdateJourneyV2,
} from '@shory-health-individual/customer-api';
import { ChannelType, ErrorCode, PolicyType, Relationship, SalaryBands } from '../../../../common';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { JourneyDetails } from '../../../../server';
import {
	selectIsRedirectFormIcp,
	selectSetDocumentError,
	selectSetIsOfflineSubmitted,
	selectSetIsRedirectFormIcp,
	selectSetJourneyId,
	useQuoteStore,
} from '../../../../stores';
import { usePolicyTypes, useSystemConfigurationSettings } from '../../../app-init-data';
import { usePolicyTypeFieldsStructure } from '../../members';
import {
	useEidCommonFieldsStructure,
	useEidDependentsFieldsStructure,
	useEidDomesticWorkerFieldsStructure,
	useEidPrincipalFieldsStructure,
} from './fields-structures';
import { aggregateValidations } from './helpers/aggregate-validations';
import { GetEidDependentValidationError, IEidMembersForm } from './types';

type UseMembersFormProps = {
	onSuccess: ({ journeyId }: { journeyId: string }) => void;
	journey?: JourneyDetails;
	handleErrorScroll: () => void;
	maximumNoOfMember: number;
	isWebView: boolean;
};

const addEmirateIdPrefix = (emirateId: string) => {
	return `${!emirateId.startsWith('784') ? '784' : ''}${emirateId}`;
};

const removeEmirateIdPrefix = (emirateId?: string) => {
	return String(emirateId).startsWith('784') ? emirateId?.slice(3) : emirateId;
};

const isInvalidEnumValue = (form: ReactForm<IEidMembersForm>, field: Path<IEidMembersForm>) =>
	form.getFieldState(field).error?.type === z.ZodIssueCode.invalid_enum_value;

const mapInitialDependents = (
	dependents: JourneyDetails['dependents'],
	policyType: PolicyType,
	supportedPolicyTypes: PolicyType[]
) => {
	const defaultDependents = [
		{
			isWithEID: 1,
		},
	];

	if (!dependents || !dependents?.length || !supportedPolicyTypes.includes(policyType)) {
		return defaultDependents;
	}

	return dependents?.map((d) => {
		return {
			...d,
			emiratesIdNumber: removeEmirateIdPrefix(d.emiratesIdNumber),
			isWithEID: d.emiratesIdNumber ? 1 : 0,
		};
	});
};

const getInitialValues = (
	members: JourneyDetails | undefined,
	policyTypes: {
		type: string;
		id: number;
	}[],
	isEffectiveDateExpired: boolean
): IEidMembersForm => ({
	policyTypeId: policyTypes.length === 1 ? policyTypes[0].id : members?.policyType,
	planCategoryId: members?.quotePlanCategoryId,
	emirateId: members?.emirateId,
	policyEffectiveDate: isEffectiveDateExpired
		? undefined
		: members?.effectiveDateFrom ?? getIsoDateTime(getTzTodayDate()),
	principalDetails: {
		...(members?.principalDetails ?? {}),
		emiratesIdNumber: removeEmirateIdPrefix(members?.principalDetails?.emiratesIdNumber),
	},
	dependents: mapInitialDependents(members?.dependents, members?.policyType as PolicyType, [
		PolicyType.OnlyFamily,
		PolicyType.MeAndFamily,
	]),
	domesticWorker: mapInitialDependents(members?.dependents, members?.policyType as PolicyType, [
		PolicyType.DomesticWorker,
	])?.[0],
});

const getPlanCategoryId = (formValues: IEidMembersForm) => {
	const salaryBandId =
		formValues.policyTypeId === PolicyType.DomesticWorker
			? formValues.domesticWorker?.salaryBandId
			: formValues.principalDetails?.salaryBandId;

	return salaryBandId === SalaryBands.BelowOrUpTo4000 ? formValues.planCategoryId : undefined;
};

export const useEidMembersForm = ({
	onSuccess,
	journey,
	handleErrorScroll,
	maximumNoOfMember,
	isWebView,
}: UseMembersFormProps) => {
	const { apiConnectionOptions, onGeneralErrorHandling, translate: t } = useAppPlatformContextOptions();
	const setJourneyId = useQuoteStore(selectSetJourneyId);
	const setIsOfflineSubmitted = useQuoteStore(selectSetIsOfflineSubmitted);
	const { systemConfigurationSettings } = useSystemConfigurationSettings();
	const [isOpen, setIsOpen] = useState(false);
	const [validationStatusCheckTimer, setValidationStatusCheckTimer] = useState(0);
	const apiTimeOutInSeconds = systemConfigurationSettings?.apiTimeOutInSeconds ?? 0;
	const commonFields = useEidCommonFieldsStructure();
	const [errorCode, setErrorCode] = useState<ErrorCode | undefined>(undefined);
	const principalFields = useEidPrincipalFieldsStructure();
	const domesticWorkerFields = useEidDomesticWorkerFieldsStructure();
	const dependentsFields = useEidDependentsFieldsStructure();
	const { policyTypes } = usePolicyTypes();
	const [openItems, setOpenItems] = useState<React.Key[]>([0]);
	const setDocumentError = useQuoteStore(selectSetDocumentError);
	const isRedirectFromIcp = useQuoteStore(selectIsRedirectFormIcp);
	const setIsRedirectFormIcp = useQuoteStore(selectSetIsRedirectFormIcp);
	const policyTypesIcons = {
		[PolicyType.OnlyMe]: SfFigureStandIcon,
		[PolicyType.MeAndFamily]: SfFigure2AndChildHoldinghandsIcon,
		[PolicyType.OnlyFamily]: SfFigureAndChildHoldinghandsIcon,
		[PolicyType.DomesticWorker]: SfFigureWalkIcon,
	};
	const getPolicyTypeOptions = (
		policyTypes: {
			id: PolicyType;
			type: string;
		}[]
	): RadioCardOption<number>[] =>
		policyTypes.map((p) => ({
			id: p.id,
			label: p.type,
			iconStart: policyTypesIcons?.[p.id],
		}));
	const policyTypeOptions = getPolicyTypeOptions(policyTypes);
	const policyTypeFieldsStructure = usePolicyTypeFieldsStructure({ policyTypeOptions });
	const effectiveDateFrom = parseIsoDateTime(journey?.effectiveDateFrom);
	const isEffectiveDateExpired = effectiveDateFrom ? isBefore(effectiveDateFrom, getTzTodayDate()) : false;

	const getValidations = (data: IEidMembersForm) =>
		aggregateValidations(
			data,
			commonFields.getValidations,
			principalFields.getValidations,
			dependentsFields.getValidations,
			domesticWorkerFields.getValidations,
			maximumNoOfMember
		);

	const getDependents = (formValues: IEidMembersForm) => {
		switch (formValues.policyTypeId) {
			case PolicyType.MeAndFamily:
			case PolicyType.OnlyFamily:
				return formValues.dependents?.map((d) => ({
					maritalStatusId: d.maritalStatusId!,
					dateOfBirth: d.dateOfBirth!,
					emiratesIdNumber: d.isWithEID ? addEmirateIdPrefix(d.emiratesIdNumber ?? '') : undefined,
					nationalityId: !d.isWithEID ? d.nationalityId : undefined,
					relationshipTypeId: d.relationshipTypeId!,
					memberId: d.memberId!,
				}));
			case PolicyType.DomesticWorker:
				return [
					{
						genderId: formValues.domesticWorker!.genderId!,
						dateOfBirth: formValues.domesticWorker!.dateOfBirth!,
						salaryBandId: formValues.domesticWorker!.salaryBandId!,
						relationshipTypeId: Relationship.DomesticWorker,
						memberId: formValues.domesticWorker!.memberId!,
						maritalStatusId: formValues.domesticWorker!.maritalStatusId!,
						nationalityId: !formValues.domesticWorker!.isWithEID ? formValues.domesticWorker!.nationalityId : undefined,
						emiratesIdNumber: formValues.domesticWorker!.isWithEID
							? addEmirateIdPrefix(formValues.domesticWorker?.emiratesIdNumber ?? '')
							: undefined,
					},
				];
			default:
				return undefined;
		}
	};

	const getChannelType = () => {
		if (isRedirectFromIcp) return ChannelType.Icp;
		else if (isWebView) return ChannelType.MobileApp;
		else return ChannelType.Website;
	};

	const form = useReactForm<IEidMembersForm>({
		initialValues: getInitialValues(journey, policyTypes, isEffectiveDateExpired),
		validationMode: 'zod',
		validation: getValidations,
		validateBehavior: 'on-submit',
		onFormSubmit: async (formValues) => {
			const membersData = {
				policyTypeId: formValues.policyTypeId!,
				channelTypeId: getChannelType(),
				emirateId: formValues.emirateId!,
				planCategoryId: getPlanCategoryId(formValues),
				policyEffectiveDate: formValues.policyEffectiveDate!,
				principalDetails: {
					maritalStatusId: formValues.principalDetails.maritalStatusId!,
					genderId: formValues.principalDetails.genderId!,
					dateOfBirth: formValues.principalDetails.dateOfBirth!,
					emiratesIdNumber: addEmirateIdPrefix(formValues.principalDetails.emiratesIdNumber!),
					memberId: formValues.principalDetails.memberId ?? '',
					salaryBandId:
						formValues.policyTypeId !== PolicyType.DomesticWorker
							? formValues.principalDetails.salaryBandId
							: undefined,
				},
				dependents: getDependents(formValues),
			};

			try {
				let response = null;

				if (journey?.encryptedJourneyId) {
					const updatedData = {
						...membersData,
						channelTypeId: journey?.channelTypeId,
						encryptedJourneyId: journey?.encryptedJourneyId,
					};

					response = await postUpdateJourneyV2(apiConnectionOptions)(updatedData);
				} else {
					response = await postCreateJourneyV2(apiConnectionOptions)(membersData);
					setJourneyId(response.encryptedJourneyId);
					setIsRedirectFormIcp(false);
				}

				if (response) {
					setValidationStatusCheckTimer(addSeconds(getNow(), apiTimeOutInSeconds).getTime());
					setIsOpen(true);

					await eidAndPersonalInfoCheck(
						apiConnectionOptions,
						apiTimeOutInSeconds * 1000
					)({
						parameters: { encryptedJourneyId: response.encryptedJourneyId },
					});
					setIsOpen(false);
					await onSuccess({ journeyId: response.encryptedJourneyId });
					setIsOfflineSubmitted(false);
					setDocumentError(undefined);
				}
			} catch (error) {
				setDocumentError(undefined);
				const errors = (error as eidAndPersonalInfoCheckError | CreateJourneyV2Error).mapped;

				if (errors.some((error) => error.code === ErrorCode.MaxNumberOfMembers)) {
					setErrorCode(ErrorCode.MaxNumberOfMembers);
				} else if (errors.some((error) => error.code === ErrorCode.InvalidDataError)) {
					setDocumentError(ErrorCode.InvalidDataError);
				} else if (errors.some((error) => error.code === ErrorCode.DataEntryError)) {
					setDocumentError(ErrorCode.DataEntryError);
				} else if (
					errors.some((error) => error.code === ErrorCode.TechnicalError || error.code === ErrorCode.ApiTimeOut)
				) {
					setDocumentError(ErrorCode.TechnicalError);
				} else if (errors.some((error) => error.code === ErrorCode.InvalidMemberNationality)) {
					const policyTypeId = form.watchValues('policyTypeId');

					if (policyTypeId === PolicyType.OnlyMe || policyTypeId === PolicyType.MeAndFamily) {
						setErrorCode(ErrorCode.InvalidNationalityForMe);
						form.setFieldError('policyTypeId', EmptyValidationMessage);
					} else {
						setErrorCode(ErrorCode.InvalidNationalityForDependents);
					}
				} else {
					await onGeneralErrorHandling(error as GlobalAPIError);
				}
			}
		},
	});

	const dependents = JSON.stringify(form.getValues('dependents'));
	const isDependentsHasError = form.getFieldState('dependents').invalid;
	const dependentFieldError = form.fieldsErrors.dependents;
	const isMaxMemberCountReachedError = dependentFieldError?.type === ZodIssueCode.too_big;

	const policyTypeId = form.getValues('policyTypeId');

	const getDependentValidation: GetEidDependentValidationError = async () => {
		const formFieldError = await form.validateField('dependents');

		if (!Array.isArray(formFieldError) && formFieldError && formFieldError.type === ZodIssueCode.too_big) {
			setErrorCode(ErrorCode.MaxNumberOfMembers);
			handleErrorScroll();

			return 'too_big';
		}

		if (formFieldError) {
			return 'form_error';
		}
	};

	useEffect(() => {
		getDependentValidation();
	}, [isMaxMemberCountReachedError, dependents]);

	useEffect(() => {
		setErrorCode(undefined);
		getDependentValidation();
	}, [policyTypeId]);

	useEffect(() => {
		if (isDependentsHasError) form.validateField('dependents');
	}, [dependents, isDependentsHasError]);

	const validateMemberDetails = () => {
		const journeyDetails = form.getValues();

		switch (journeyDetails.policyTypeId) {
			case PolicyType.OnlyMe: {
				if (isInvalidEnumValue(form, 'principalDetails.nationalityId')) {
					setErrorCode(ErrorCode.InvalidNationalityForMe);
				}

				if (isInvalidEnumValue(form, 'principalDetails.emiratesIdNumber')) {
					setDocumentError(ErrorCode.WrongEidFormat);
				}
				break;
			}
			case PolicyType.MeAndFamily: {
				if (
					isInvalidEnumValue(form, 'principalDetails.nationalityId') ||
					journeyDetails.dependents?.some((_, index) => isInvalidEnumValue(form, `dependents.${index}.nationalityId`))
				) {
					setErrorCode(ErrorCode.InvalidNationalityForMe);
				}

				if (
					isInvalidEnumValue(form, 'principalDetails.emiratesIdNumber') ||
					journeyDetails.dependents?.some(
						(dependent, index) =>
							dependent.isWithEID && isInvalidEnumValue(form, `dependents.${index}.emiratesIdNumber`)
					)
				) {
					setDocumentError(ErrorCode.WrongEidFormat);
				}
				break;
			}
			case PolicyType.OnlyFamily: {
				if (
					journeyDetails.dependents?.some((_, index) => isInvalidEnumValue(form, `dependents.${index}.nationalityId`))
				) {
					setErrorCode(ErrorCode.InvalidNationalityForDependents);
				}

				if (
					isInvalidEnumValue(form, 'principalDetails.emiratesIdNumber') ||
					journeyDetails.dependents?.some(
						(dependent, index) =>
							dependent.isWithEID && isInvalidEnumValue(form, `dependents.${index}.emiratesIdNumber`)
					)
				) {
					setDocumentError(ErrorCode.WrongEidFormat);
				}
				break;
			}
			case PolicyType.DomesticWorker: {
				if (isInvalidEnumValue(form, 'domesticWorker.nationalityId')) {
					setErrorCode(ErrorCode.InvalidNationalityForDependents);
				}

				if (
					isInvalidEnumValue(form, 'principalDetails.emiratesIdNumber') ||
					(journeyDetails.domesticWorker?.isWithEID && isInvalidEnumValue(form, 'domesticWorker.emiratesIdNumber'))
				) {
					setDocumentError(ErrorCode.WrongEidFormat);
				}
				break;
			}
		}
	};

	const onSubmit = useCallback(async (): Promise<void> => {
		setDocumentError(undefined);
		setErrorCode(undefined);
		form.setFieldError('policyTypeId', undefined);
		await form.submitForm();
		validateMemberDetails();
	}, []);

	useEffect(() => {
		!journey && setDocumentError(undefined);
		isEffectiveDateExpired &&
			form.setFieldError('policyEffectiveDate', t('members:policy-effective-date.validation.effective-date-expired'));
	}, [t]);

	return {
		form,
		isOpen,
		onSubmit,
		openItems,
		errorCode,
		setOpenItems,
		getDependentValidation,
		policyTypeFieldsStructure,
		isMaxMemberCountReachedError,
		validationStatusCheckTimer,
	};
};
